import { NotesPanel, ReceiptRejection, UpgradeSubscriptionModal } from '@components'

import { Button, ButtonsGroup, notify, Tooltip, useModal, useModals } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { AttachmentType } from '@features/attachments/enums/attachmentType'
import { BillState } from '@features/bills/enums/billState'
import { Bill } from '@features/bills/types/bill'
import { EInvoiceStatus } from '@features/eInvoicing/constants/eInvoiceStatus'
import { EInvoiceLifecycleActions } from '@features/eInvoicing/elements/EInvoiceLifecycleActions'
import { useFREInvoiceStatusInfo } from '@features/eInvoicing/hooks/useFREInvoiceStatusInfo'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { EInvoiceLifecycleTrackingEvents, EInvoiceLifecycleTrackingSources } from '@views/receipts/enums/tracking'

import { OPEN_NOTES_PANEL_SHORTCUT, SUBMIT_SHORTCUT } from '../../../../constants/shortcuts'
import { useDirtyRoute } from '../../../../contexts/dirtyRouteContext'
import { ModalId } from '../../../../enums/modalId'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useFormContext, useSegment, useWatch } from '../../../../hooks'
import { useComponentId, useOrganizationBrand, useSubscriptionLimits } from '../../../../hooks/'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useBillAttachmentPageContext } from '../../contexts/billAttachmentPageContext'
import { BillSubmitAction } from '../../enums/billSubmitAction'
import { BulkMode } from '../../enums/bulkMode'
import { useBillTimeline } from '../../hooks/useBillTimeline'
import { useBulkBillsNavigation } from '../../hooks/useBulkBillsNavigation'
import { BillBulkEditNavigation } from '../BillBulkEditNavigation'
import { BillDeleteAction } from '../BillDeleteAction'
import { BillDuplicationsModal } from '../BillDuplicationsModal'
import { useBillForm } from '../BillEditableForm/contexts/billFormContext'
import { useBillDuplicatesCheck } from '../BillEditableForm/hooks/useBillDuplicatesCheck'
import { useDeleteBillAction } from '../BillEditableForm/hooks/useDeleteBillAction'
import { BillFormSchema } from '../BillEditableForm/utils/formData'
import { isBillApproveAction } from '../BillEditableForm/utils/isBillApproveAction'
import { BillNotesPanel } from '../BillNotesPanel'
import { DeleteBillConfirmationModal } from '../DeleteBillConfirmationModal'

const PENDING_VENDOR_CREATION_GRACE_PERIOD_MS = 1000

export const BillEditableActions = (): ReactElement | null => {
  const {
    approve,
    billId,
    eInvoiceStatus,
    isFetching,
    isLifecycleLocked,
    modalDisplayMode,
    processingAction,
    save,
    bulkEditMode,
  } = useBillForm()

  const isRefused = eInvoiceStatus === EInvoiceStatus.Refused

  const [duplicatedBills, setDuplicatedBills] = useState<Bill[]>()
  const [isNotesPanelOpen, setIsNotesPanelOpen] = useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { checkForDuplicateBills, isChecking: isCheckingDuplicates } = useBillDuplicatesCheck()
  const { hasExceededExpensesLimit, invalidateLimits, expensesLimit } = useSubscriptionLimits()
  const { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal } = useModal(
    ModalId.BillDeleteConfirmationModal,
  )
  const { open: openDuplicateBillModal } = useModal(ModalId.DuplicatedBillActionModal, {
    onClose: () => setDuplicatedBills(undefined),
  })
  const { open: openCreateContactModal, isOpen: isCreateContactModalOpen } = useModal(ModalId.CreateContactModal)
  const [isApprovePendingVendorCreation, setIsApprovePendingVendorCreation] = useState(false)
  const upgradeModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openUpgradeSubscriptionModal } = useModal(upgradeModalId)
  const { setDirty } = useDirtyRoute()
  const { hasNextBill, goToNextBill } = useBulkBillsNavigation()
  const [{ openedIds: openedModalsIds }] = useModals()
  const { track } = useSegment()
  const { control } = useFormContext<BillFormSchema>()
  const [currentPageIndex] = useBillAttachmentPageContext()
  const billState = useWatch({ control, name: 'state' })
  const attachmentsFiles = useWatch({ control, name: 'attachmentsFiles', defaultValue: [] })
  const vendor = useWatch({ control, name: 'vendor' })
  const { count: notesCount } = useBillTimeline(billId)
  const { isBrandBilly } = useOrganizationBrand()
  const { organization } = useUserOrganization()
  const { url: billsUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getMainRoute())
  const { url: uploadsUrl } = useOrganizationViewUrl(reactRoute.organization.uploads.getMainRoute())

  // Computed values

  const isApprovalProcessing = isBillApproveAction(processingAction)
  const isSaveProcessing = processingAction === BillSubmitAction.Save
  const isDraftBillOrBillBulk = billState === BillState.Draft || bulkEditMode === BulkMode.Bill // isBulkBillEditMode to faster detect drafts
  const isCurrentFileEDocument = attachmentsFiles?.[currentPageIndex]?.attachmentType === AttachmentType.EDocument
  // Dispute/suspend and the reworked CTA layout are for the French market only
  // Other markets keep the legacy refuse flow (c.f. ReceiptRejection)
  const isFrenchEInvoice = isCurrentFileEDocument && organization?.countryId === 'FR'
  const currentAttachmentId = attachmentsFiles?.[currentPageIndex]?.attachmentId

  const { isFinal: isFREInvoiceStatusFinal } = useFREInvoiceStatusInfo(eInvoiceStatus) || { isFinal: false }

  // Delete mutation

  const { deleteBill, isDeleting } = useDeleteBillAction({
    onSuccess: async () => {
      closeDeleteConfirmationModal()
      if (bulkEditMode === BulkMode.Bill && hasNextBill) {
        goToNextBill()
      } else {
        // Route back to the bills list
        navigate(`${billsUrl}${location.search}`, { replace: true })
      }
    },
  })

  // Helpers

  const approveBillLabel = useMemo(() => {
    if (bulkEditMode) {
      return t('bill.action.approve_and_next')
    }

    if (billState === BillState.Approved) {
      return t('bill.action.update_approved')
    }

    if (modalDisplayMode) {
      return t('bill.action.save')
    }

    return t('bill.action.approve')
  }, [billState, bulkEditMode, modalDisplayMode, t])

  // Handlers

  const handleSaveClick = useCallback(() => {
    track('XXX - Jesper - Action Taken', {
      context: 'draft_bill_view',
      action: 'save draft',
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy event, naming convention was likely not checked back then
      bills_beta: true,
    })

    if (isFetching) {
      return
    }

    save()
  }, [isFetching, save, track])

  const handleOpenCreateContactModal = useCallback(() => {
    setIsApprovePendingVendorCreation(true)
    openCreateContactModal()
  }, [openCreateContactModal])

  const handleApprove = useCallback(async () => {
    if (hasExceededExpensesLimit) {
      handleSaveClick()

      if (isBrandBilly) {
        openUpgradeSubscriptionModal()
      } else {
        notify({
          id: 'expenses-limit-exceeded',
          message: t('premium_feature.feature.free_expenses_limit.body', { limit: expensesLimit }),
          variant: 'error',
        })
      }
      return
    }

    if (vendor && !vendor.id) {
      handleOpenCreateContactModal()
      return
    }

    if (billState === BillState.Approved) {
      approve()
      invalidateLimits()
      return
    }

    const duplicates = await checkForDuplicateBills()
    if (duplicates.length > 0) {
      setDuplicatedBills(duplicates)
      openDuplicateBillModal()
    } else {
      approve()
      invalidateLimits()
    }
  }, [
    approve,
    billState,
    checkForDuplicateBills,
    expensesLimit,
    handleOpenCreateContactModal,
    handleSaveClick,
    hasExceededExpensesLimit,
    invalidateLimits,
    isBrandBilly,
    openDuplicateBillModal,
    openUpgradeSubscriptionModal,
    t,
    vendor,
  ])

  useEffect(() => {
    if (!isCreateContactModalOpen && isApprovePendingVendorCreation) {
      if (vendor?.id) {
        setIsApprovePendingVendorCreation(false)
        handleApprove()
      } else {
        const timeoutId = setTimeout(() => {
          setIsApprovePendingVendorCreation(false)
        }, PENDING_VENDOR_CREATION_GRACE_PERIOD_MS)

        return () => clearTimeout(timeoutId)
      }
    }
  }, [isCreateContactModalOpen, isApprovePendingVendorCreation, vendor?.id, handleApprove])

  const handleApproveShortcut = useCallback(() => {
    handleApprove()
  }, [handleApprove])

  const handleApproveClick = useCallback(() => {
    track('XXX - Jesper - Action Taken', {
      context: 'draft_bill_view',
      action: 'approve',
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy event, naming convention was likely not checked back then
      bills_beta: true,
    })

    if (isFrenchEInvoice) {
      track(EInvoiceLifecycleTrackingEvents.ApproveCTAClicked, {
        source: EInvoiceLifecycleTrackingSources.ExpensesEditor,
      })
    }

    if (isFetching) {
      return
    }

    handleApprove()
  }, [handleApprove, isFetching, isFrenchEInvoice, track])

  const handleDeleteClick = useCallback(() => {
    track('XXX - Jesper - Action Taken', {
      context: 'draft_bill_view',
      action: 'delete draft',
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy event, naming convention was likely not checked back then
      bills_beta: true,
    })
    openDeleteConfirmationModal()
  }, [openDeleteConfirmationModal, track])

  const handleDeleteBill = useCallback(() => {
    if (!billId) {
      navigate(`${billsUrl}${location.search}`, { replace: true })
      return
    }

    deleteBill(billId)
  }, [billId, billsUrl, deleteBill, navigate, location.search])

  const handleOnDuplicateKeep = useCallback(() => {
    setDirty(false)
    approve()
  }, [approve, setDirty])

  const handleOnDuplicateDiscard = useCallback(() => {
    setDirty(false)
    if (bulkEditMode === BulkMode.Attachment && !billId) {
      return
    }
    handleDeleteBill()
  }, [billId, bulkEditMode, handleDeleteBill, setDirty])

  const handleAddNotesButtonClick = useCallback(() => {
    setIsNotesPanelOpen(true)
  }, [])

  const handleNotesPanelClose = useCallback(() => {
    setIsNotesPanelOpen(false)
  }, [])

  const handleOpenNotesPanelShortcut = useCallback(() => {
    setIsNotesPanelOpen(true)
  }, [])

  const handleEInvoiceLifecycleSuccess = useCallback(() => {
    if (uploadsUrl) {
      navigate(uploadsUrl)
    }
  }, [uploadsUrl, navigate])

  // Shortcuts

  useHotkeys(
    SUBMIT_SHORTCUT,
    handleApproveShortcut,
    {
      enableOnTags: ['INPUT', 'TEXTAREA', 'SELECT'],
      enabled: !isRefused && !isLifecycleLocked && !openedModalsIds.length && !isNotesPanelOpen,
    },
    [isRefused, isLifecycleLocked, openedModalsIds, isNotesPanelOpen],
  )

  useHotkeys(
    OPEN_NOTES_PANEL_SHORTCUT,
    handleOpenNotesPanelShortcut,
    { enabled: !!billId && !isFetching, enableOnTags: ['INPUT', 'SELECT', 'TEXTAREA'] },
    [billId, isFetching, handleOpenNotesPanelShortcut],
  )

  if (isRefused || isLifecycleLocked) {
    return null
  }

  return (
    <>
      <ButtonsGroup>
        {isFrenchEInvoice && !isFREInvoiceStatusFinal && (
          <EInvoiceLifecycleActions
            attachmentId={currentAttachmentId}
            disabled={isFetching}
            onSuccess={handleEInvoiceLifecycleSuccess}
            previousStatus={eInvoiceStatus}
            trackingSource={EInvoiceLifecycleTrackingSources.ExpensesEditor}
          />
        )}
        <Button
          disabled={isFetching || (!!processingAction && !isApprovalProcessing)}
          icon="circleCheck"
          loading={isApprovalProcessing || isCheckingDuplicates}
          onClick={handleApproveClick}
        >
          {approveBillLabel}
        </Button>
        {isCurrentFileEDocument && !isFrenchEInvoice && (
          <ReceiptRejection
            attachmentId={currentAttachmentId}
            onSuccess={handleEInvoiceLifecycleSuccess}
            trackingSource={EInvoiceLifecycleTrackingSources.ExpensesEditor}
          />
        )}
        {/*
          We have to disable saving as draft in the attachment's bulk edit, as we don't have billId at this point.
          The url and whole mechanism of this bulk is based on the attachments ids so we would have to handle it additionally
          to also keep the billId in the url after creation. It's doable, but for now we're skipping this case.
        */}
        {!isFrenchEInvoice &&
          bulkEditMode !== BulkMode.Attachment &&
          billState !== BillState.Approved &&
          !modalDisplayMode && (
            <Button
              disabled={isFetching || (!!processingAction && !isSaveProcessing)}
              icon="uploadCloud"
              loading={isSaveProcessing}
              onClick={handleSaveClick}
              variant="outlined"
            >
              {billId ? t('bill.action.update') : t('bill.action.save_as_draft')}
            </Button>
          )}
        {billId && !isFetching && (
          <Tooltip label={t('note_panel.title')} placement="bottom">
            <NotesPanel.Trigger onClick={handleAddNotesButtonClick} count={notesCount} />
          </Tooltip>
        )}
        {isDraftBillOrBillBulk && (
          <BillDeleteAction tooltipMessage={t('bills.edit.delete.message.cpa')} onClick={handleDeleteClick} />
        )}
        {!!bulkEditMode && <BillBulkEditNavigation />}
      </ButtonsGroup>

      {/* Delete Confirmation Modal  */}
      <DeleteBillConfirmationModal
        id={ModalId.BillDeleteConfirmationModal}
        onOk={handleDeleteBill}
        isDeleting={isDeleting}
      />

      {/* Bill Duplication Modal  */}
      <BillDuplicationsModal
        duplicatedBills={duplicatedBills}
        onOk={handleOnDuplicateKeep}
        onCancel={handleOnDuplicateDiscard}
      />

      {/* Notes Panel */}
      {billId && <BillNotesPanel billId={billId} isOpen={isNotesPanelOpen} onClose={handleNotesPanelClose} />}

      {/* Upgrade Subscription Modal */}
      <UpgradeSubscriptionModal id={upgradeModalId} trackingContext={TrackingContext.FreeExpensesLimitExceeded} />
    </>
  )
}
